import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Copy, Plus, Trash2, Link2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DiscountsTab = ({ pw }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "",
    percent_off: 15,
    scope: "packages",
    max_uses: 1,
    note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/discounts`, {
        headers: { "X-Admin-Password": pw },
      });
      setCodes(data.codes || []);
    } catch {
      toast.error("Couldn't load discount codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [pw]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error("Code is required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/discounts`, form, {
        headers: { "X-Admin-Password": pw },
      });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: "", percent_off: 15, scope: "packages", max_uses: 1, note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create code");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (code, active) => {
    try {
      await axios.patch(
        `${API}/admin/discounts/${encodeURIComponent(code)}?active=${active}`,
        {},
        { headers: { "X-Admin-Password": pw } }
      );
      load();
    } catch {
      toast.error("Couldn't toggle code");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/discounts/${encodeURIComponent(code)}`, {
        headers: { "X-Admin-Password": pw },
      });
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Couldn't delete code");
    }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/?code=${code}#packages`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied`);
  };

  return (
    <div className="space-y-8" data-testid="admin-discounts-tab">
      {/* Create form */}
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl border border-[var(--line)] p-6 shadow-sm"
      >
        <h3 className="font-display text-lg font-extrabold mb-4 flex items-center gap-2">
          <Plus size={18} /> Mint a new discount code
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1">
              Code
            </label>
            <input
              data-testid="discount-create-code"
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="MATES15"
              className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-mono uppercase"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1">
              % Off
            </label>
            <input
              data-testid="discount-create-percent"
              type="number"
              min="1"
              max="100"
              value={form.percent_off}
              onChange={(e) => setForm({ ...form, percent_off: parseInt(e.target.value || "0", 10) })}
              className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1">
              Scope
            </label>
            <select
              data-testid="discount-create-scope"
              value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value })}
              className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
            >
              <option value="packages">Packages</option>
              <option value="subscription">Subscription</option>
              <option value="guides">Guides</option>
              <option value="all">All</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1">
              Max uses
            </label>
            <input
              data-testid="discount-create-max-uses"
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value || "1", 10) })}
              className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
            />
            <p className="text-[10px] text-[var(--ocean)]/70 mt-1">1 = single-use · 999999 = share-link</p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1">
              Internal note
            </label>
            <input
              data-testid="discount-create-note"
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Beta tester batch 1"
              className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
            />
          </div>
        </div>
        <button
          type="submit"
          data-testid="discount-create-submit"
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-bold rounded-lg px-5 py-2.5 text-sm hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
        >
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Create code
        </button>
      </form>

      {/* Codes list */}
      <div className="bg-white rounded-2xl border border-[var(--line)] overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-[var(--line)] flex items-center justify-between">
          <h3 className="font-display text-lg font-extrabold">All codes</h3>
          <span className="text-xs text-[var(--ocean)]">{codes.length} total</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[var(--ocean)]">
            <Loader2 size={20} className="animate-spin inline" />
          </div>
        ) : codes.length === 0 ? (
          <p className="p-8 text-center text-sm text-[var(--ocean)]">No codes yet. Create one above.</p>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--mist-soft)] text-[10px] uppercase tracking-widest text-[var(--ocean)]">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">% Off</th>
                  <th className="text-left px-4 py-3">Scope</th>
                  <th className="text-left px-4 py-3">Used / Max</th>
                  <th className="text-left px-4 py-3">Expires</th>
                  <th className="text-left px-4 py-3">Note</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {codes.map((c) => (
                  <tr key={c.code} data-testid={`discount-row-${c.code}`} className={c.active ? "" : "opacity-50"}>
                    <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                    <td className="px-4 py-3">{c.percent_off}%</td>
                    <td className="px-4 py-3 capitalize">{c.scope}</td>
                    <td className="px-4 py-3">
                      {c.uses} / {c.max_uses >= 999999 ? "∞" : c.max_uses}
                      {c.once_per_email && <div className="text-[10px] text-[var(--ocean)]">Once per employer</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--ocean)]">{c.expires_at ? new Date(c.expires_at).toLocaleString() : "Never"}</td>
                    <td className="px-4 py-3 text-xs text-[var(--ocean)]">{c.note || "—"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggle(c.code, !c.active)}
                        data-testid={`discount-toggle-${c.code}`}
                        className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${
                          c.active ? "bg-[var(--lime)] text-[var(--ink)]" : "bg-[var(--mist-soft)] text-[var(--ocean)]"
                        }`}
                      >
                        {c.active ? "Active" : "Disabled"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => copyCode(c.code)}
                          title="Copy code"
                          className="p-1.5 rounded hover:bg-[var(--mist-soft)] transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => copyLink(c.code)}
                          title="Copy share link"
                          className="p-1.5 rounded hover:bg-[var(--mist-soft)] transition-colors"
                        >
                          <Link2 size={14} />
                        </button>
                        <button
                          onClick={() => remove(c.code)}
                          title="Delete"
                          data-testid={`discount-delete-${c.code}`}
                          className="p-1.5 rounded hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-[var(--line)] md:hidden">
            {codes.map((c) => (
              <div key={c.code} data-testid={`discount-mobile-row-${c.code}`} className={`p-4 space-y-3 ${c.active ? "" : "opacity-50"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-sm font-bold text-[var(--ink)]">{c.code}</p>
                    <p className="mt-1 text-xs text-[var(--ocean)]">{c.percent_off}% off · {c.scope}</p>
                  </div>
                  <button onClick={() => toggle(c.code, !c.active)} data-testid={`discount-toggle-mobile-${c.code}`} className={`shrink-0 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${c.active ? "bg-[var(--lime)] text-[var(--ink)]" : "bg-[var(--mist-soft)] text-[var(--ocean)]"}`}>{c.active ? "Active" : "Disabled"}</button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="block text-[10px] uppercase tracking-widest text-[var(--ocean)]">Usage</span><strong>{c.uses} / {c.max_uses >= 999999 ? "∞" : c.max_uses}</strong>{c.once_per_email && <span className="block text-[10px] text-[var(--ocean)]">Once per employer</span>}</div>
                  <div><span className="block text-[10px] uppercase tracking-widest text-[var(--ocean)]">Expires</span><strong>{c.expires_at ? new Date(c.expires_at).toLocaleString() : "Never"}</strong></div>
                </div>
                <p className="text-xs text-[var(--ocean)]">{c.note || "No internal note"}</p>
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => copyCode(c.code)} data-testid={`discount-copy-mobile-${c.code}`} title="Copy code" className="p-2 rounded hover:bg-[var(--mist-soft)]"><Copy size={14}/></button>
                  <button onClick={() => copyLink(c.code)} data-testid={`discount-link-mobile-${c.code}`} title="Copy package link" className="p-2 rounded hover:bg-[var(--mist-soft)]"><Link2 size={14}/></button>
                  <button onClick={() => remove(c.code)} data-testid={`discount-delete-mobile-${c.code}`} title="Delete" className="p-2 rounded hover:bg-red-50 hover:text-red-600"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      <p className="text-xs text-[var(--ocean)]/70 flex items-start gap-2">
        <Check size={12} className="mt-0.5 shrink-0 text-[var(--lime-deep)]" />
        Currently active for: <span className="font-bold">Packages (Stripe + PayPal)</span>. Subscription &amp; Guides scope-ready — wire on request.
      </p>
    </div>
  );
};
